import { HttpHeaders } from '@angular/common/http';
import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { GeoCountryResponse, GeoQuery, GeoService, IdentityProfileQuery, ProfileOrg } from '@b3networks/api/auth';
import { Pageable } from '@b3networks/api/common';
import {
  GetReportV4Payload,
  LoginHistoryService,
  Period,
  ReportV4Code,
  UserDataLoginHistory,
  V4Service
} from '@b3networks/api/data';
import { SessionService } from '@b3networks/portal/base/shared';
import { DestroySubscriberComponent, MessageConstants, X_B3_HEADER } from '@b3networks/shared/common';
import { ToastService } from '@b3networks/shared/ui/toast';
import { addDays, startOfDay, subDays } from 'date-fns';
import { format } from 'date-fns-tz';
import { catchError, distinctUntilKeyChanged, filter, finalize, forkJoin, map, of, takeUntil } from 'rxjs';

@Component({
  selector: 'b3n-login-activity',
  templateUrl: 'login-activity.component.html',
  styleUrls: ['login-activity.component.scss']
})
export class LoginActivityComponent extends DestroySubscriberComponent implements OnInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Output() gotoPasswordSettingsEvent = new EventEmitter();

  hasOtherSessions: boolean;
  currentCountry: string;
  currentIpAddress: string;
  signoutSuccessful = false;
  loading = true;
  fetching: boolean;
  pageable: Pageable = { page: 1, perPage: 10 };
  ui = {
    paging: new Pageable(1, 10),
    backUpPrevious: <UserDataLoginHistory[]>null,
    currentActivities: <UserDataLoginHistory[]>[],
    backUpNext: <UserDataLoginHistory[]>null,
    message: 'No data available'
  };
  displayedColumns = ['device', 'location', 'status', 'duration', 'datetime'];

  org: ProfileOrg;
  signingOut: boolean;

  constructor(
    private sessionService: SessionService,
    private geoService: GeoService,
    private toastService: ToastService,
    private geoQuery: GeoQuery,
    private profileQuery: IdentityProfileQuery,
    private loginHistoryService: LoginHistoryService,
    private v4Service: V4Service
  ) {
    super();
  }

  ngOnInit() {
    this.geoService.getGeoInfo().subscribe();
    this.geoQuery.geo$.subscribe((response: GeoCountryResponse) => {
      this.updateGeoInfo(response);
    });

    this.initData();
  }

  private initData() {
    this.loading = true;
    this.signoutSuccessful = false;
    this.profileQuery.currentOrg$
      .pipe(
        filter(o => o != null),
        distinctUntilKeyChanged('orgUuid')
      )
      .subscribe(org => {
        this.org = org;

        this.fetchDataV4();
      });
  }

  private updateGeoInfo(data: GeoCountryResponse) {
    if (!data.isDefault) {
      this.currentCountry = data.countryName;
      this.currentIpAddress = data.ipAddress;
    }
  }

  signOutOtherLoginSession() {
    this.signingOut = true;
    this.sessionService.logout().subscribe(
      _ => {
        this.signingOut = false;
        this.signoutSuccessful = true;
      },
      _ => {
        this.signingOut = false;
        this.toastService.warning(MessageConstants.GENERAL_ERROR);
      }
    );
  }

  gotoPasswordSettings() {
    this.gotoPasswordSettingsEvent.emit('gotoPasswordSettings');
  }

  private fetchDataV4() {
    this.fetching = true;

    const next = Object.assign({}, this.ui.paging);
    next.page++;
    const endTime = format(startOfDay(addDays(new Date(), 1)), "yyyy-MM-dd'T'HH:mm:ssxxx", {
      timeZone: this.org.utcOffset
    });
    const startTime = format(startOfDay(subDays(new Date(), 30)), "yyyy-MM-dd'T'HH:mm:ssxxx", {
      timeZone: this.org.utcOffset
    });

    const headers = new HttpHeaders().set(X_B3_HEADER.orgUuid, '');
    forkJoin([
      this.v4Service
        .getReportData<UserDataLoginHistory>(
          Period.dump,
          ReportV4Code.userManageLoginHistory,
          <GetReportV4Payload>{
            startTime: startTime,
            endTime: endTime,
            scope: 'personal'
          },
          this.ui.paging,
          false,
          headers
        )
        .pipe(
          map(resp => resp?.rows),
          catchError(_ => of(null))
        ),
      this.v4Service
        .getReportData<UserDataLoginHistory>(
          Period.dump,
          ReportV4Code.userManageLoginHistory,
          <GetReportV4Payload>{
            startTime: startTime,
            endTime: endTime,
            scope: 'personal'
          },
          next,
          false,
          headers
        )
        .pipe(
          map(resp => resp?.rows),
          catchError(_ => of(null))
        )
    ])
      .pipe(
        takeUntil(this.destroySubscriber$),
        finalize(() => {
          this.loading = false;
          this.fetching = false;
        })
      )
      .subscribe({
        next: ([current, backup]) => {
          this.ui.currentActivities = current;
          this.ui.backUpNext = backup;
        }
      });
  }

  prevPage() {
    this.fetching = true;
    this.ui.backUpNext = this.ui.currentActivities;
    this.ui.currentActivities = this.ui.backUpPrevious;
    this.ui.backUpPrevious = null;
    this.ui.paging.page--;
    this.loadMore(false);
  }

  nextPage() {
    this.fetching = true;
    this.ui.backUpPrevious = this.ui.currentActivities;
    this.ui.currentActivities = this.ui.backUpNext;
    this.ui.backUpNext = null;
    this.ui.paging.page++;
    this.loadMore(true);
  }

  private loadMore(isNext: boolean) {
    const handlePage = Object.assign({}, this.ui.paging);
    handlePage.page = isNext ? handlePage.page + 1 : handlePage.page - 1;
    const endTime = format(startOfDay(addDays(new Date(), 1)), "yyyy-MM-dd'T'HH:mm:ssxxx", {
      timeZone: this.org.utcOffset
    });
    const startTime = format(startOfDay(subDays(new Date(), 30)), "yyyy-MM-dd'T'HH:mm:ssxxx", {
      timeZone: this.org.utcOffset
    });

    const headers = new HttpHeaders().set(X_B3_HEADER.orgUuid, '');
    return this.v4Service
      .getReportData<UserDataLoginHistory>(
        Period.dump,
        ReportV4Code.userManageLoginHistory,
        <GetReportV4Payload>{
          startTime: startTime,
          endTime: endTime,
          scope: 'personal'
        },
        handlePage,
        false,
        headers
      )
      .pipe(
        takeUntil(this.destroySubscriber$),
        finalize(() => {
          this.fetching = false;
          this.loading = false;
        }),
        map(resp => resp?.rows),
        catchError(_ => of(null))
      )
      .subscribe({
        next: histories => {
          if (isNext) {
            this.ui.backUpNext = histories;
          } else {
            this.ui.backUpPrevious = histories;
          }
        },
        error: err => {
          this.toastService.error(err.message);
        }
      });
  }
}
